import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { createTakeaway, type TakeawayResponse } from "../api/userTakeaways";
import useRetryFetch from "./useRetryFetch";
import { queryClient } from "../lib/queryClient";

/**
 * Shared hook for saving user takeaways (insights or action items).
 * Used by GrowthFeed, CompanionMessageCTAs, and other components.
 */
export function useSaveTakeaway() {
  const retryFetch = useRetryFetch();
  const { t } = useTranslation();

  const save = useCallback(
    async (
      type: "insight" | "action",
      content: string,
      opts?: {
        onSuccess?: () => void;
        onError?: () => void;
        spaceIds?: string[];
        dueDate?: number;
        successMessage?: string;
        /** Skip the built-in success toast — the caller shows its own (e.g. with a reclassify action). */
        suppressToast?: boolean;
      }
    ): Promise<TakeawayResponse | undefined> => {
      try {
        const result = await createTakeaway(retryFetch, {
          type,
          content,
          source: { type: "user_created" },
          spaceIds: opts?.spaceIds,
          dueDate: opts?.dueDate,
        });
        if (!opts?.suppressToast) {
          toast.success(
            opts?.successMessage ??
              (type === "insight" ? t("insight_saved") : t("action_item_saved"))
          );
        }
        if (type === "action") {
          queryClient.invalidateQueries({ queryKey: ["home-today-actions"] });
        }
        opts?.onSuccess?.();
        return result;
      } catch {
        toast.error(
          type === "insight"
            ? t("insight_save_error", "Failed to save insight")
            : t("action_save_error", "Failed to save action item")
        );
        opts?.onError?.();
        return undefined;
      }
    },
    [retryFetch, t]
  );

  return save;
}
